import React, { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Layers, ChevronDown, KeyRound, X } from 'lucide-react';

// Badges des modules rattachés à un QCM (liste)
export const QcmModulesBadges = ({ modules = [], qcmId }) => {
  if (!modules.length) return <span className="text-slate-300">—</span>;
  return (
    <div className="flex flex-wrap gap-1" data-testid={`qcm-modules-${qcmId}`}>
      {modules.map(m => (
        <span key={m.id} title={`${m.code} — ${m.titre}${m.is_cle ? ' (Test clé)' : ''}`} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${m.is_cle ? 'bg-amber-100 text-amber-800' : 'bg-violet-100 text-violet-700'}`}>
          {m.is_cle && <KeyRound className="w-2.5 h-2.5" />}{m.code}
        </span>
      ))}
    </div>
  );
};

// Sélecteur multi-modules (builder) : coche/décoche les modules auxquels rattacher le QCM
export const QcmModulesPicker = ({ modules = [], selectedIds = [], onChange, disabled }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const selected = modules.filter(m => selectedIds.includes(m.id));
  const filtres = modules.filter(m => !q || `${m.code} ${m.titre}`.toLowerCase().includes(q.toLowerCase()));
  const toggle = (id) => onChange(selectedIds.includes(id) ? selectedIds.filter(x => x !== id) : [...selectedIds, id]);
  return (
    <div className="space-y-2" data-testid="qcm-modules-picker">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" disabled={disabled} className="w-[280px] h-10 inline-flex items-center justify-between gap-2 rounded-md border border-white/20 bg-white/10 px-3 text-sm text-white disabled:opacity-60" data-testid="qcm-modules-trigger">
            <span className="inline-flex items-center gap-2 truncate"><Layers className="w-4 h-4 shrink-0" />{selected.length ? `${selected.length} module${selected.length > 1 ? 's' : ''} rattaché${selected.length > 1 ? 's' : ''}` : 'Rattacher à des modules...'}</span>
            <ChevronDown className="w-4 h-4 shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[380px] p-0" data-testid="qcm-modules-liste">
          <div className="p-2 border-b"><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un module (code, titre)…" className="h-8 text-sm" data-testid="qcm-modules-recherche" /></div>
          <div className="max-h-64 overflow-y-auto">
            {filtres.map(m => (
              <label key={m.id} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer text-sm" data-testid={`qcm-module-option-${m.id}`}>
                <Checkbox checked={selectedIds.includes(m.id)} onCheckedChange={() => toggle(m.id)} />
                <span className="min-w-0 truncate"><span className="font-mono text-[11px] text-violet-600 mr-1.5">{m.code}</span>{m.titre}</span>
              </label>
            ))}
            {filtres.length === 0 && <p className="px-3 py-4 text-xs text-slate-400 text-center">Aucun module</p>}
          </div>
        </PopoverContent>
      </Popover>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map(m => (
            <span key={m.id} className="inline-flex items-center gap-1 rounded-full bg-white/15 text-white text-[11px] pl-2 pr-1 py-0.5" data-testid={`qcm-module-chip-${m.id}`}>
              <span className="font-mono">{m.code}</span><span className="text-white/70 max-w-[160px] truncate">{m.titre}</span>
              {!disabled && <button type="button" onClick={() => toggle(m.id)} className="rounded-full hover:bg-white/20 p-0.5" aria-label={`Détacher ${m.code}`} data-testid={`qcm-module-remove-${m.id}`}><X className="w-3 h-3" /></button>}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};
